import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from "axios";
import { getAccessToken, getRefreshToken, setAfterLoginRedirectURL } from "./localStorage";
import { refreshToken } from "./login";
import { LogoutRoute } from "@/routes/auth";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleError(error: AxiosError): any {
  const statusCode = error.response?.status;
  const endpoint = error.request?.responseURL ?? "";
  let errorMessage = `${error.message}`;
  if (endpoint?.includes("tariff/withdrawal/candidate?changeServiceId")) {
    errorMessage = `Tarrif not found!`;
  }

  // pass endpoint in case of statusCode 0
  if (statusCode === 0) {
    errorMessage = `StatusCode ${statusCode}, ${error.message}, endpoint: ${endpoint}`;
  }

  console.error("Request Error:", errorMessage);
  
  const customError = new Error(errorMessage);
  customError.name = statusCode === undefined ? "Request Error" : `StatusCode ${statusCode}`;
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apiMessage = (error.response?.data as any)?.message;
  if (typeof apiMessage === 'string' && apiMessage.trim() !== '') {
    customError.message = apiMessage.trim();
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (customError as any).status = statusCode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (customError as any).response = error.response;
  throw customError;
}

const request = axios.create();

let isRefreshRequesting = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let requestsToRefresh: any = [];

const responseInterceptor = request.interceptors.response.use(
  (response) => response,
  // For each requests, do something when the server sends back an error
  async (error) => {
    const { response, config } = error;
    const currentURL = new URL(window.location.href);

    // User access token are missing. No need to get a refresh token
    if (!getRefreshToken()) {
      throw new Error(`Couldn't find refresh token with [${response?.config?.url}]`);
    }

    // User is authorized but the server is not authorizing the request
    // Token is expired or invalid
    if (response?.status === 401) {
      // Eject the interceptor so it doesn't loop in case the token refresh
      // causes again a 401 response
      request.interceptors.response.eject(responseInterceptor);
      const refresh_token = getRefreshToken();
      if (!isRefreshRequesting && refresh_token) {
        isRefreshRequesting = true;
        // Request a new token
        refreshToken(refresh_token)
          .then((data) => {
            // When new token is ready:
            // Retries all the failed requests by
            // triggering all pending promises callback with this new valid token
            requestsToRefresh.forEach((requestCallback: AxiosInstance) => requestCallback(data.access_token));
          })
          .catch(() => {
            requestsToRefresh.forEach((requestCallback: (token: string | null) => AxiosError) => requestCallback(null));
            setAfterLoginRedirectURL(currentURL.pathname);
            window.open(LogoutRoute, "_self");
          })
          .finally(() => {
            // Clear queue of failed requests
            requestsToRefresh = [];
            isRefreshRequesting = false;
          });
      }

      return new Promise((resolve, reject) => {
        const requestCallback = (token: string | null) => {
          // Patch the new token to all the future request re-attempt
          if (token) {
            config.headers.Authorization = "Bearer " + token;
            // Re-trigger the request on promise resolve
            resolve(request(config));
          }
          reject(error);
        };

        // Save all request re-attempts for later when token is ready
        requestsToRefresh.push(requestCallback);
      });
    }

    return Promise.reject(error);
  }
);

export const GetRequest = async (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
  const access_token = getAccessToken();
  const token = access_token && `Bearer ${access_token}`;
  return (
    request
      // ES6 for just adding token when given to object
      .get(url, {
        ...(config && { ...config }),
        ...(token && { headers: { Authorization: token } }),
      })
      .then((response) => response)
      .catch(handleError)
  );
};

export const GetRequestPublic = async (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
  return (
    request
      // ES6 for just adding token when given to object
      .get(url, {
        ...(config && { ...config }),
      })
      .then((response) => response)
      .catch(handleError)
  );
};

export const PostRequest = async (
  url: string,
  data: object | string,
  config?: AxiosRequestConfig,
  headers?: AxiosRequestHeaders
): Promise<AxiosResponse> => {
  const access_token = getAccessToken();
  const token = access_token && `Bearer ${access_token}`;
  return (
    request
      // ES6 for just adding token when given to object
      .post(url, data, {
        ...config,
        headers: { ...(token && { Authorization: token }), ...(headers && { ...headers }) },
      })
      .then((response) => response)
      .catch(handleError)
  );
};


export const PostRequestPublic = async (
  url: string,
  data: object | string,
  config?: AxiosRequestConfig,
  headers?: AxiosRequestHeaders
): Promise<AxiosResponse> => {
  return (
    axios
      .post(url, data, {
        ...config,
        headers: { ...(headers && { ...headers }) },
      })
      .then((response) => response)
      .catch(handleError) // Still use the same global error handler
  );
};

export const PutRequest = async (
  url: string,
  data: object,
  config?: AxiosRequestConfig,
  headers?: AxiosRequestHeaders
): Promise<AxiosResponse> => {
  const access_token = getAccessToken();
  const token = access_token && `Bearer ${access_token}`;
  return (
    request
      // ES6 for just adding token when given to object
      .put(url, data, {
        ...config,
        headers: { ...(token && { Authorization: token }), ...(headers && { ...headers }) },
      })
      .then((response) => response)
      .catch(handleError)
  );
};

export const PutRequestImpersonator = async (
  url: string,
  data: object,
  config?: AxiosRequestConfig,
  headers?: AxiosRequestHeaders,
  impersonator_token?: string | undefined
): Promise<AxiosResponse> => {
  const token = `Bearer ${impersonator_token?.replace(/"/g, "")}`;
  return (
    request
      // ES6 for just adding token when given to object
      .put(url, data, {
        ...config,
        headers: { ...(token && { Authorization: token }), ...(headers && { ...headers }) },
      })
      .then((response) => response)
      .catch(handleError)
  );
};

export const PatchRequest = async (
  url: string,
  data: object,
  config?: AxiosRequestConfig,
  headers?: AxiosRequestHeaders
): Promise<AxiosResponse> => {
  const access_token = getAccessToken();
  const token = access_token && `Bearer ${access_token}`;
  return (
    request
      // ES6 for just adding token when given to object
      .patch(url, data, {
        ...config,
        headers: { ...(token && { Authorization: token }), ...(headers && { ...headers }) },
      })
      .then((response) => response)
      .catch(handleError)
  );
};

export const DeleteRequest = async (url: string, config?: AxiosRequestConfig) => {
  const access_token = getAccessToken();
  const token = access_token && `Bearer ${access_token}`;
  return (
    request
      // ES6 for just adding token when given to object
      .delete(url, { ...config, ...(token && { headers: { Authorization: token } }) })
      .then((response) => response)
      .catch(handleError)
  );
};
